// src/storage/comprehensionStorage.ts — Local-First Persistence for Comprehension Station
// Implements NFR-DATA-001, FR-ENG-002, FR-ENG-003.

export interface ClueAttempt {
  passageId: string;
  selectedSentenceIndex: number;
  correct: boolean;
  attemptNumber: number;
  timestamp: number;
}

export interface VocabAttempt {
  vocabId: string;
  word: string;
  questionId: string;
  selectedOptionIndex: number;
  correct: boolean;
  timestamp: number;
}

export interface RetrievalSession {
  passageId: string;
  correctCount: number;
  totalQuestions: number;
  timeSpentSeconds: number;
  timestamp: number;
}

export interface ComprehensionProgressData {
  clueAttempts: ClueAttempt[];
  streakCount: number;
  bestStreak: number;
  solvedPassageIds: string[];
  totalCluesFound: number;
  lastSessionDate: string;
  vocabAttempts: VocabAttempt[];
  masteredVocabWords: string[];
  retrievalSessions: RetrievalSession[];
}

const STORAGE_KEY = 'clawey_comprehension_progress_v1';

function getDefaultProgress(): ComprehensionProgressData {
  return {
    clueAttempts: [],
    streakCount: 0,
    bestStreak: 0,
    solvedPassageIds: [],
    totalCluesFound: 0,
    lastSessionDate: new Date().toISOString().split('T')[0] ?? '',
    vocabAttempts: [],
    masteredVocabWords: [],
    retrievalSessions: [],
  };
}

export class ComprehensionStorage {
  /** Load saved comprehension progress from localStorage */
  static load(): ComprehensionProgressData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return getDefaultProgress();
      const parsed = JSON.parse(raw) as Partial<ComprehensionProgressData>;
      const defaults = getDefaultProgress();
      return {
        ...defaults,
        ...parsed,
        clueAttempts: Array.isArray(parsed.clueAttempts) ? [...parsed.clueAttempts] : [],
        solvedPassageIds: Array.isArray(parsed.solvedPassageIds) ? [...parsed.solvedPassageIds] : [],
        vocabAttempts: Array.isArray(parsed.vocabAttempts) ? [...parsed.vocabAttempts] : [],
        masteredVocabWords: Array.isArray(parsed.masteredVocabWords) ? [...parsed.masteredVocabWords] : [],
        retrievalSessions: Array.isArray(parsed.retrievalSessions) ? [...parsed.retrievalSessions] : [],
      };
    } catch (err) {
      console.warn('Failed to load comprehension progress:', err);
      return getDefaultProgress();
    }
  }

  /** Save comprehension progress to localStorage */
  static save(data: ComprehensionProgressData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn('Failed to save comprehension progress:', err);
    }
  }

  /** Record a clue hunt attempt and update stats */
  static recordAttempt(attempt: ClueAttempt): ComprehensionProgressData {
    const data = this.load();
    data.clueAttempts.push(attempt);

    if (attempt.correct) {
      data.streakCount += 1;
      if (data.streakCount > data.bestStreak) {
        data.bestStreak = data.streakCount;
      }
      if (!data.solvedPassageIds.includes(attempt.passageId)) {
        data.solvedPassageIds.push(attempt.passageId);
        data.totalCluesFound += 1;
      }
    } else {
      data.streakCount = 0;
    }

    this.save(data);
    return data;
  }

  /** Record a vocabulary check attempt */
  static recordVocabAttempt(attempt: VocabAttempt): ComprehensionProgressData {
    const data = this.load();
    data.vocabAttempts.push(attempt);

    if (attempt.correct && !data.masteredVocabWords.includes(attempt.word.toLowerCase())) {
      data.masteredVocabWords.push(attempt.word.toLowerCase());
    }

    if (attempt.correct) {
      data.streakCount += 1;
      if (data.streakCount > data.bestStreak) {
        data.bestStreak = data.streakCount;
      }
    } else {
      data.streakCount = 0;
    }

    this.save(data);
    return data;
  }

  /** Record a completed retrieval exercise session */
  static recordRetrievalSession(session: RetrievalSession): ComprehensionProgressData {
    const data = this.load();
    data.retrievalSessions.push(session);

    if (session.correctCount === session.totalQuestions) {
      data.streakCount += 1;
      if (data.streakCount > data.bestStreak) {
        data.bestStreak = data.streakCount;
      }
    }

    this.save(data);
    return data;
  }

  /** Get list of mastered vocabulary words */
  static getMasteredVocabWords(): string[] {
    const data = this.load();
    return [...data.masteredVocabWords];
  }

  /** Reset all comprehension progress */
  static reset(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore in private mode
    }
  }
}
